/**
 * Coverage change amendment API processing
 */

import { processAmendment } from '../../shared/amendmentHelpers';

/**
 * Process coverage change amendment
 *
 * @param {Object} quotationPayload - The new quotation data
 * @param {Object} policy - Current policy object
 * @param {String} oldCoverageType - Current coverage type
 * @param {String} newCoverageType - Target coverage type
 * @param {Array} removedMembers - Members being removed (for downgrade)
 * @param {String} newPrincipalName - Name of final Principal
 * @param {String} oldPrincipalName - Name of original Principal
 * @param {Boolean} isPrincipalSwapped - Whether Principal was changed
 * @param {Function} updateStatus - Callback to update loading status
 * @returns {Promise} - Resolves when amendment is complete
 */
export const processCoverageChangeAmendment = async (
  quotationPayload,
  policy,
  oldCoverageType,
  newCoverageType,
  removedMembers,
  newPrincipalName,
  oldPrincipalName,
  isPrincipalSwapped,
  updateStatus
) => {
  try {
    // Calculate premium details
    const oldPremium = policy.quotation.policy_schedule_entries
      .reduce((sum, entry) => sum + (entry.premium_amount_in_cents || 0), 0) / 100;
    const newPremium = quotationPayload.policy_schedule_entries
      .reduce((sum, entry) => sum + (entry.premium_amount_in_cents || 0), 0) / 100;
    const premiumDiff = newPremium - oldPremium;
    const changeType = premiumDiff > 0 ? 'Increase' : (premiumDiff < 0 ? 'Decrease' : 'No Change');

    // Build sections for single comprehensive note
    const principalSection = isPrincipalSwapped ? `Principal:
New: ${newPrincipalName}
Previous: ${oldPrincipalName}

` : '';

    const removedSection = (removedMembers && removedMembers.length > 0) ? `Removed (${removedMembers.length}):
${removedMembers.map(m => `* ${m.first_name} ${m.last_name} (${m.relationship})`).join('\n')}

` : '';

    // Build single comprehensive note
    const noteContent = `COVERAGE TYPE CHANGE

Policy: ${policy.policy_number}

Change:
From: ${oldCoverageType}
To: ${newCoverageType}

${principalSection}${removedSection}Premium:
Old: BWP ${oldPremium.toFixed(2)}
New: BWP ${newPremium.toFixed(2)}
${changeType}: BWP ${Math.abs(premiumDiff).toFixed(2)}

Total: ${quotationPayload.policy_schedule_entries.length} members`;

    // Use shared amendment helper
    return await processAmendment({
      quotationPayload,
      policy,
      noteContent,
      updateStatus
    });
  } catch (error) {
    console.error('Coverage change amendment error:', error);
    throw new Error(
      error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : error.response && error.response.data && error.response.data.error
        ? error.response.data.error
        : 'Failed to process coverage change. Please try again.'
    );
  }
};
